const formatMissingField = (field) => String(field || '').replace(/_/g, ' ').trim();

const toStringList = (value) => {
    if (Array.isArray(value)) {
        return value.flatMap(toStringList);
    }

    if (typeof value === 'string') {
        return value
            .split(/\r?\n|[•●▪◦]/)
            .map((item) => item.trim())
            .filter(Boolean);
    }

    if (value && typeof value === 'object') {
        return Object.values(value).flatMap(toStringList);
    }

    return [];
};

const isBookingRelated = (value) => /\b(?:book|books|booked|booking|bookings|reservation|reservations|reserva|reservas|reservar|appointment|appointments|cita|citas|agendar|agendamento|agendamentos)\b/i.test(String(value || ''));

export const getFollowUpItems = (data = {}) => {
    const safeData = data && typeof data === 'object' ? data : {};
    const questions = toStringList(safeData.questions).filter((question) => !isBookingRelated(question));
    if (questions.length) return questions;

    return toStringList(safeData.missing_info)
        .filter((field) => !isBookingRelated(field))
        .map(formatMissingField)
        .filter(Boolean);
};

export const PARTICIPATING_COUNTRIES = [
    { id: '11', name: 'Panamá', code: 'PA', aliases: ['panama', 'panama city', 'ciudad de panama', 'pty'] },
    { id: '12', name: 'Colombia', code: 'CO', aliases: ['colombia', 'bogota', 'bogota dc', 'medellin', 'cali', 'barranquilla', 'cartagena'] },
    { id: '14', name: 'México', code: 'MX', aliases: ['mexico', 'cdmx', 'ciudad de mexico', 'guadalajara', 'monterrey', 'cancun', 'puebla'] },
    { id: '15', name: 'Costa Rica', code: 'CR', aliases: ['costa rica', 'san jose', 'heredia', 'alajuela'] },
    { id: '16', name: 'El Salvador', code: 'SV', aliases: ['el salvador', 'san salvador', 'santa ana'] },
    { id: '17', name: 'Perú', code: 'PE', aliases: ['peru', 'lima', 'cusco', 'arequipa', 'trujillo'] },
    { id: '18', name: 'Brasil', code: 'BR', aliases: ['brasil', 'brazil', 'sao paulo', 'rio de janeiro', 'rio', 'brasilia'] },
    { id: '19', name: 'Argentina', code: 'AR', aliases: ['argentina', 'buenos aires', 'cordoba', 'rosario', 'mendoza'] },
];

export const stripAccents = (str = '') =>
    String(str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

export const normalizeCountryId = (input, countriesList = []) => {
    if (!input) return undefined;
    const str = stripAccents(input);
    if (!str) return undefined;

    const foundById = PARTICIPATING_COUNTRIES.find((c) => c.id === str);
    if (foundById) return foundById.id;

    if (Array.isArray(countriesList) && countriesList.length) {
        const foundFromList = countriesList.find((c) =>
            String(c.id) === str ||
            stripAccents(c.name) === str ||
            stripAccents(c.code) === str
        );
        if (foundFromList) return String(foundFromList.id);
    }

    const foundByCode = PARTICIPATING_COUNTRIES.find((c) => stripAccents(c.code) === str);
    if (foundByCode) return foundByCode.id;

    const foundByName = PARTICIPATING_COUNTRIES.find((c) => stripAccents(c.name) === str);
    if (foundByName) return foundByName.id;

    const foundByAlias = PARTICIPATING_COUNTRIES.find((c) =>
        c.aliases.some((alias) => alias === str || str.includes(alias) || alias.includes(str))
    );
    if (foundByAlias) return foundByAlias.id;

    return undefined;
};

export const getCountryDisplayName = (countryId, countriesList = []) => {
    if (!countryId) return '';
    const idStr = String(countryId);
    if (Array.isArray(countriesList) && countriesList.length) {
        const found = countriesList.find((c) => String(c.id) === idStr);
        if (found) return found.name;
    }
    const found = PARTICIPATING_COUNTRIES.find((c) => c.id === idStr);
    return found ? found.name : idStr;
};

export const inferCountryFromText = (text = '', countriesList = []) => {
    if (!text) return undefined;
    const normalized = stripAccents(text);

    for (const country of PARTICIPATING_COUNTRIES) {
        const countryNameNorm = stripAccents(country.name);
        const nameRegex = new RegExp(`\\b${countryNameNorm}\\b`, 'i');
        if (nameRegex.test(normalized)) {
            return country.id;
        }
        for (const alias of country.aliases) {
            const aliasRegex = new RegExp(`\\b${alias}\\b`, 'i');
            if (aliasRegex.test(normalized)) {
                return country.id;
            }
        }
    }

    if (Array.isArray(countriesList) && countriesList.length) {
        for (const c of countriesList) {
            const nameNorm = stripAccents(c.name);
            if (new RegExp(`\\b${nameNorm}\\b`, 'i').test(normalized)) {
                return String(c.id);
            }
        }
    }

    return undefined;
};

export const monthNames = {
    january: 0,
    jan: 0,
    enero: 0,
    janeiro: 0,
    february: 1,
    feb: 1,
    febrero: 1,
    fevereiro: 1,
    march: 2,
    mar: 2,
    marzo: 2,
    marco: 2,
    april: 3,
    apr: 3,
    abril: 3,
    may: 4,
    mayo: 4,
    maio: 4,
    june: 5,
    jun: 5,
    junio: 5,
    junho: 5,
    july: 6,
    jul: 6,
    julio: 6,
    julho: 6,
    august: 7,
    aug: 7,
    agosto: 7,
    september: 8,
    sep: 8,
    sept: 8,
    septiembre: 8,
    setembro: 8,
    october: 9,
    oct: 9,
    octubre: 9,
    outubro: 9,
    november: 10,
    nov: 10,
    noviembre: 10,
    novembro: 10,
    december: 11,
    dec: 11,
    diciembre: 11,
    dezembro: 11,
};

export const toISODate = (year, monthIndex, day) => {
    const y = Number(year);
    const m = Number(monthIndex);
    const d = Number(day);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return undefined;
    const pad = (n) => String(n).padStart(2, '0');
    return `${y}-${pad(m + 1)}-${pad(d)}`;
};

export const extractDateRange = (text = '', referenceDate = new Date()) => {
    const normalized = stripAccents(text);
    const now = referenceDate instanceof Date && !Number.isNaN(referenceDate.getTime()) ? referenceDate : new Date();
    const currentYear = now.getFullYear();
    const monthAlternates = Object.keys(monthNames).join('|');

    let result = {};

    // 1. Spanish format: "del 15 al 25 de octubre" / "15 al 25 de octubre 2026"
    const spanishRange = new RegExp(`\\b(?:del\\s+|de\\s+|desde\\s+)?(\\d{1,2})\\s+(?:al|a|hasta)\\s+(\\d{1,2})\\s+(?:de\\s+)?(${monthAlternates})(?:\\s+(?:del?|of)?\\s*(\\d{4}))?\\b`, 'i');
    const matchSp = normalized.match(spanishRange);
    if (matchSp) {
        const startDay = Number(matchSp[1]);
        const endDay = Number(matchSp[2]);
        const month = monthNames[matchSp[3].toLowerCase()];
        let year = matchSp[4] ? Number(matchSp[4]) : currentYear;
        const testDate = new Date(year, month, startDay);
        if (!matchSp[4] && testDate < now) {
            year += 1;
        }
        result = {
            from_date: toISODate(year, month, startDay),
            to_date: toISODate(year, month, endDay),
        };
    }

    // 1b. Multi-month Spanish format: "del 1 de octubre al 30 de noviembre"
    if (!result.from_date) {
        const multiMonthSpRange = new RegExp(`\\b(?:del\\s+|de\\s+|desde\\s+)?(\\d{1,2})\\s+(?:de\\s+)?(${monthAlternates})\\s+(?:al|a|hasta|to)\\s+(?:el\\s+)?(\\d{1,2})\\s+(?:de\\s+)?(${monthAlternates})(?:\\s+(?:del?|of)?\\s*(\\d{4}))?\\b`, 'i');
        const matchMultiMonthSp = normalized.match(multiMonthSpRange);
        if (matchMultiMonthSp) {
            const startDay = Number(matchMultiMonthSp[1]);
            const startMonth = monthNames[matchMultiMonthSp[2].toLowerCase()];
            const endDay = Number(matchMultiMonthSp[3]);
            const endMonth = monthNames[matchMultiMonthSp[4].toLowerCase()];
            let year = matchMultiMonthSp[5] ? Number(matchMultiMonthSp[5]) : currentYear;
            const testDate = new Date(year, startMonth, startDay);
            if (!matchMultiMonthSp[5] && testDate < now) {
                year += 1;
            }
            let endYear = year;
            if (endMonth < startMonth) {
                endYear += 1;
            }
            result = {
                from_date: toISODate(year, startMonth, startDay),
                to_date: toISODate(endYear, endMonth, endDay),
            };
        }
    }

    // 2. Format: "octubre 15 al 25" / "octubre 15 al noviembre 5"
    if (!result.from_date) {
        const sameMonthRange = new RegExp(`\\b(?:from\\s+|del\\s+|de\\s+|desde\\s+)?(${monthAlternates})\\s+(\\d{1,2})\\s+(?:to|through|until|al|a|hasta)\\s+(?:(${monthAlternates})\\s+)?(\\d{1,2})(?:\\s+(?:of|de)?\\s*(\\d{4}))?\\b`, 'i');
        const match = normalized.match(sameMonthRange);
        if (match) {
            const startMonth = monthNames[match[1].toLowerCase()];
            const startDay = Number(match[2]);
            const endMonth = match[3] ? monthNames[match[3].toLowerCase()] : startMonth;
            const endDay = Number(match[4]);
            let year = match[5] ? Number(match[5]) : currentYear;
            const testDate = new Date(year, startMonth, startDay);
            if (!match[5] && testDate < now) {
                year += 1;
            }
            result = {
                from_date: toISODate(year, startMonth, startDay),
                to_date: toISODate(year, endMonth, endDay),
            };
        }
    }

    // 3. Relative: "este fin de semana" / "this weekend" / "este sabado" / "proximo fin de semana"
    if (!result.from_date) {
        if (/proximo\s+fin\s*(?:de\s*semana)?|next\s+weekend/i.test(normalized)) {
            const dayOfWeek = now.getDay();
            const daysUntilSat = (6 - dayOfWeek + 7) % 7 + 7;
            const sat = new Date(now);
            sat.setDate(now.getDate() + (daysUntilSat === 7 ? 7 : daysUntilSat));
            const sun = new Date(sat);
            sun.setDate(sat.getDate() + 1);
            result = {
                from_date: toISODate(sat.getFullYear(), sat.getMonth(), sat.getDate()),
                to_date: toISODate(sun.getFullYear(), sun.getMonth(), sun.getDate()),
            };
        } else if (/este\s+fin\s*(?:de\s*semana)?|this\s+weekend|este\s+sabado|este\s+domingo/i.test(normalized)) {
            const dayOfWeek = now.getDay();
            const daysUntilSat = (6 - dayOfWeek + 7) % 7;
            const sat = new Date(now);
            sat.setDate(now.getDate() + (daysUntilSat === 0 && now.getHours() >= 18 ? 7 : daysUntilSat));
            const sun = new Date(sat);
            sun.setDate(sat.getDate() + 1);
            result = {
                from_date: toISODate(sat.getFullYear(), sat.getMonth(), sat.getDate()),
                to_date: toISODate(sun.getFullYear(), sun.getMonth(), sun.getDate()),
            };
        } else if (/proxima\s+semana|next\s+week/i.test(normalized)) {
            const dayOfWeek = now.getDay();
            const daysUntilMonday = (8 - dayOfWeek) % 7 || 7;
            const mon = new Date(now);
            mon.setDate(now.getDate() + daysUntilMonday);
            const sun = new Date(mon);
            sun.setDate(mon.getDate() + 6);
            result = {
                from_date: toISODate(mon.getFullYear(), mon.getMonth(), mon.getDate()),
                to_date: toISODate(sun.getFullYear(), sun.getMonth(), sun.getDate()),
            };
        } else if (/\b(?:a\s+partir\s+de\s+)?(?:manana|tomorrow)\b/i.test(normalized)) {
            const start = new Date(now);
            start.setDate(now.getDate() + 1);
            const end = new Date(start);
            const daysMatch = normalized.match(/por\s+(\d+)\s+dias?/i);
            if (daysMatch) {
                const days = parseInt(daysMatch[1], 10);
                end.setDate(start.getDate() + Math.min(days, 30));
            } else if (/por\s+(?:una|1)\s+semana|for\s+(?:a|1)\s+week/i.test(normalized)) {
                end.setDate(start.getDate() + 7);
            } else {
                end.setDate(start.getDate() + 14);
            }
            result = {
                from_date: toISODate(start.getFullYear(), start.getMonth(), start.getDate()),
                to_date: toISODate(end.getFullYear(), end.getMonth(), end.getDate()),
            };
        }
    }

    // 4. Numeric range: "15/10 to 25/10"
    if (!result.from_date) {
        const numericRange = normalized.match(/\b(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?\s+(?:to|through|until|al|a|hasta)\s+(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?\b/i);
        if (numericRange) {
            const startYear = numericRange[3]?.length === 2 ? `20${numericRange[3]}` : numericRange[3] || currentYear;
            const endYear = numericRange[6]?.length === 2 ? `20${numericRange[6]}` : numericRange[6] || startYear;
            result = {
                from_date: toISODate(startYear, Number(numericRange[2]) - 1, numericRange[1]),
                to_date: toISODate(endYear, Number(numericRange[5]) - 1, numericRange[4]),
            };
        }
    }

    // Sanitize: swap if reversed, clamp to max 30 days
    if (result.from_date && result.to_date) {
        let fromD = new Date(`${result.from_date}T00:00:00`);
        let toD = new Date(`${result.to_date}T00:00:00`);
        if (fromD > toD) {
            const temp = fromD;
            fromD = toD;
            toD = temp;
        }
        const maxSpan = 30 * 24 * 60 * 60 * 1000;
        if (toD.getTime() - fromD.getTime() > maxSpan) {
            toD = new Date(fromD.getTime() + maxSpan);
        }
        result.from_date = toISODate(fromD.getFullYear(), fromD.getMonth(), fromD.getDate());
        result.to_date = toISODate(toD.getFullYear(), toD.getMonth(), toD.getDate());
    }

    return result;
};

export const hasExplicitDate = (text = '') => {
    const normalized = stripAccents(text);
    const datePatterns = [
        /\b\d{4}-\d{1,2}-\d{1,2}\b/,
        /\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b/,
        /\b\d{1,2}\s+(?:de\s+)?(?:enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)\b/,
        /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}\b/,
        /\b\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/,
    ];

    return datePatterns.some((pattern) => pattern.test(normalized));
};

export const hasVagueDate = (text = '') => {
    const normalized = stripAccents(text);
    return [
        'next weekend',
        'this weekend',
        'next week',
        'tomorrow',
        'weekend',
        'proximo fin',
        'este fin',
        'proxima semana',
        'manana',
        'fim de semana',
        'proximo fim',
    ].some((term) => normalized.includes(term));
};

export const normalizeGeneratedData = (data = {}, compensationMode = 'exchange', countriesList = []) => {
    const safeData = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const deliverablesMode = safeData.deliverables_mode === 'open'
        ? 'open'
        : safeData.deliverables_mode === 'defined'
            ? 'defined'
            : undefined;
    const exchangeOptions = toStringList(safeData.exchange_options);
    const targetOptions = toStringList(safeData.target_options);
    const deliverablesCounts = deliverablesMode === 'open'
        ? {}
        : safeData.deliverables_counts && typeof safeData.deliverables_counts === 'object' && !Array.isArray(safeData.deliverables_counts)
            ? safeData.deliverables_counts
            : undefined;

    const resolvedCountry = normalizeCountryId(safeData.country, countriesList);
    const sanitizedData = Object.fromEntries(
        Object.entries(safeData).filter(([key]) => key !== 'add_booking'),
    );

    return {
        ...sanitizedData,
        missing_info: toStringList(safeData.missing_info),
        questions: getFollowUpItems(safeData),
        confidence: safeData.confidence || 'medium',
        type: safeData.type ? String(safeData.type) : undefined,
        country: resolvedCountry || (safeData.country ? String(safeData.country) : undefined),
        followers_requirements: safeData.followers_requirements ? String(safeData.followers_requirements) : undefined,
        exchange_options: exchangeOptions.length ? exchangeOptions : undefined,
        target_options: targetOptions.length ? targetOptions : undefined,
        deliverables_mode: deliverablesMode,
        deliverables_counts: deliverablesCounts,
        quantity: safeData.quantity ? Number(safeData.quantity) : undefined,
        // Paid budgets must never become a product/meal benefit implicitly.
        value: ['paid', 'hybrid'].includes(compensationMode) || ['paid', 'hybrid'].includes(safeData.compensation_mode)
            ? (safeData.in_kind_value != null && Number.isFinite(Number(safeData.in_kind_value)) && Number(safeData.in_kind_value) >= 0
                ? Number(safeData.in_kind_value) : undefined)
            : (safeData.value != null ? Number(safeData.value) : undefined),
        sponsor_amount: safeData.sponsor_amount ? Number(safeData.sponsor_amount) : undefined,
        compensation_mode: safeData.compensation_mode || undefined,
        cash_currency: safeData.cash_currency ? String(safeData.cash_currency).toUpperCase() : undefined,
    };
};
